import React, { useState } from 'react'
import { Box, TableCell, TablePagination, TableRow, Typography } from '@mui/material'
import DoneIcon from '@mui/icons-material/Done'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { TableListContainer, TablePaginationActions } from '~/src/components/organisms'
import { TableLabelText } from '~/src/components/molecules'
import ImportExportTwoToneIcon from '@mui/icons-material/ImportExportTwoTone'
import { formatDataStringParam } from '~/src/utils/formatDate'
import {
  IContainersResponse,
  IQuery,
  TContainerOrdersItem,
  TContainers,
} from '~/src/repositories/Container'
import { ContainerChip } from './Chips/ContainerChip'

interface IContainerOrderProps {
  data: TContainerOrdersItem[]
  pagination: IContainersResponse['data']['pagination']
  onNextPages: (_params: IQuery) => void
}
interface IColumn {
  id:
    | 'vanningDate'
    | 'county'
    | 'code'
    | 'name'
    | 'businessPartner'
    | 'customerName'
    | 'businessNegotiations'
    | 'chargeOfInput'
    | 'invoiceNo'
    | 'ft'
    | 'collectionRate'
    | 'payment'
  label: string
  minWidth?: number
  align?: 'right'
  render: (item: TContainers, index: number) => React.ReactNode
}

export const ContainerOrder: React.FC<IContainerOrderProps> = ({
  data,
  pagination,
  onNextPages,
}): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [page, setPage] = useState((pagination?.currentPage ?? 0) - 1)
  const [rowsPerPage, setRowsPerPage] = useState(pagination?.limitValue)

  const items: TContainerOrdersItem[] = data

  const columns: IColumn[] = [
    {
      id: 'vanningDate',
      label: `${t('container_order.vanning_date')}`,
      minWidth: 140,
      render: ({ vanningDate }) => (
        <TableLabelText customStyles={{ width: '100px', marginLeft: '8px' }}>
          {formatDataStringParam(vanningDate)}
        </TableLabelText>
      ),
    },
    {
      id: 'county',
      label: `${t('container_order.country')}`,
      minWidth: 90,
      render: ({ country }) => <TableLabelText>{country}</TableLabelText>,
    },
    {
      id: 'code',
      label: `${t('container_order.customer_code')}`,
      minWidth: 130,
      render: ({ customerCode }) => <TableLabelText>{customerCode}</TableLabelText>,
    },
    {
      id: 'name',
      label: `${t('container_order.proud_first_name')}`,
      minWidth: 240,
      render: ({ companyName }) => <TableLabelText>{companyName}</TableLabelText>,
    },
    {
      id: 'businessPartner',
      label: `${t('container_order.business_partner_abbreviation')}`,
      minWidth: 210,
      render: ({ customerNameAbbreviation }) => (
        <TableLabelText>{customerNameAbbreviation}</TableLabelText>
      ),
    },
    {
      id: 'customerName',
      label: `${t('container_order.customer_name')}`,
      minWidth: 220,
      render: ({ contactPerson }) => <TableLabelText>{contactPerson}</TableLabelText>,
    },
    {
      id: 'businessNegotiations',
      label: `${t('container_order.in_charge_of_business_negotiations')}`,
      minWidth: 100,
      render: ({ salesPersonName }) => <TableLabelText>{salesPersonName}</TableLabelText>,
    },
    {
      id: 'chargeOfInput',
      label: `${t('container_order.input_charge')}`,
      minWidth: 170,
      render: ({ inputPersonName }) => <TableLabelText>{inputPersonName}</TableLabelText>,
    },
    {
      id: 'invoiceNo',
      label: `${t('container_order.invoice_no')}`,
      minWidth: 110,
      render: ({ invoiceNo }) => <TableLabelText>{invoiceNo}</TableLabelText>,
    },
    {
      id: 'ft',
      label: `${t('container_order.ft')}`,
      minWidth: 70,
      render: ({ containerSize }) => (
        <TableLabelText>{containerSize === '' ? '-' : containerSize}</TableLabelText>
      ),
    },
    {
      id: 'collectionRate',
      label: `${t('container_order.collection_rate')}`,
      minWidth: 100,
      render: ({ cargo }) => <TableLabelText>{cargo}</TableLabelText>,
    },
    {
      id: 'payment',
      label: `${t('container_order.status')}`,
      minWidth: 100,
      render: ({ status }) => <ContainerChip status={status} />,
    },
  ]

  /**
   * handleChangePage
   * @param event
   * @param newPage
   */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
    onNextPages({ limit: rowsPerPage, offset: newPage + 1 })
  }
  /**
   * handleChangeRowsPerPage
   * @param event
   */
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  return (
    <>
      <TableListContainer
        tableContainerSx={{
          background: '#FFFFFF',
          minWidth: 400,
          overflow: 'auto',
        }}
        tableHeadComponents={columns.map((column, index) => (
          <TableCell
            key={column.id}
            style={{
              minWidth: column.minWidth,
              background: '#FFFFFF',
              padding: `${index === 0 ? '0px 8px 0 16px' : '0px 8px'}`,
            }}
            sx={{ height: '56px' }}
          >
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
              <Box display={'flex'} alignItems={'center'}>
                <Typography
                  sx={{
                    fontSize: 14,
                    lineHeight: '24px',
                    fontStyle: 'normal',
                    fontWeight: 'medium',
                    alignItems: 'center',
                  }}
                >
                  {column.label}
                </Typography>
                <Box>
                  {(index === 1 || index === 3) && (
                    <ImportExportTwoToneIcon
                      sx={{
                        marginTop: 1,
                        width: '20px',
                        height: '20px',
                        color: 'rgba(0, 0, 0, 0.54)',
                      }}
                    />
                  )}
                </Box>
              </Box>
              <Typography
                sx={{
                  width: 2,
                  height: '14px',
                  backgroundColor: `${index === columns.length - 1 ? 'white' : '#E0E0E0'}`,
                }}
              />
            </Box>
          </TableCell>
        ))}
        tableBodyComponents={items.map((item: TContainerOrdersItem, index: number) => (
          <TableRow
            hover
            role="checkbox"
            tabIndex={-1}
            key={index}
            sx={{
              cursor: 'pointer',
            }}
          >
            {columns.map((column, i) => (
              <TableCell
                key={i}
                align={column.align}
                sx={
                  i === 10
                    ? { height: '49px', padding: '0px 12px 0 0' }
                    : { height: '49px', padding: '0px 10px' }
                }
                onClick={() =>
                  navigate(`/admin/order/container-order/detail/${item.id}`, {
                    state: {
                      item: item,
                      prevLocationState: { ...location },
                    },
                  })
                }
              >
                {column.render(item.containers, index)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      />
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={pagination?.totalCount ?? 0}
        rowsPerPage={rowsPerPage ?? 0}
        sx={{
          fontSize: '12px',
          fontStyle: 'normal',
          lineHeight: '20px',
          fontWeight: 'Regular',
        }}
        page={page ?? 0}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
      />
    </>
  )
}
